import { Hls } from 'hls-video-element';
import { MediaMuteButton } from 'media-chrome';

const ATTR_SRC = 'src';
const ATTR_POSTER = 'poster';
const PREFERRED_TRAILER_HEIGHT = 720;
const POSTER_MUTE_TOGGLE_EVENT = 'postermutetogglerequest';

class BunnyPosterMuteButton extends MediaMuteButton {
  handleClick() {
    this.dispatchEvent(
      new CustomEvent(POSTER_MUTE_TOGGLE_EVENT, {
        bubbles: true,
        composed: true,
      }),
    );
  }
}

if (!globalThis.customElements.get('bunny-poster-mute-button')) {
  globalThis.customElements.define(
    'bunny-poster-mute-button',
    BunnyPosterMuteButton,
  );
}

class BunnyPosterVideo extends HTMLElement {
  private videoEl: HTMLVideoElement | null = null;
  private muteButtonEl: BunnyPosterMuteButton | null = null;
  private controllerObserver: MutationObserver | null = null;
  private hls: Hls | null = null;
  private loadedSrc: string | null = null;
  private hasAudioTrack = false;
  private shouldPlay = false;
  private hasSeenLiveActive = false;
  private initialized = false;

  static get observedAttributes() {
    return [ATTR_SRC, ATTR_POSTER];
  }

  connectedCallback() {
    this.initialize();
    this.connectControllerObserver();
    this.syncVideoAttributes();
    this.syncPlaybackState();
  }

  disconnectedCallback() {
    this.disconnectControllerObserver();
    if (this.videoEl) {
      this.videoEl.pause();
    }
    this.destroyHls();
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ) {
    if (!this.initialized || oldValue === newValue) return;
    if (name === ATTR_SRC || name === ATTR_POSTER) {
      this.syncVideoAttributes();
      this.syncPlaybackState();
    }
  }

  private initialize() {
    if (this.initialized) return;

    const shadow = this.attachShadow({ mode: 'open' });
    shadow.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
          height: 100%;
          pointer-events: none;
          position: relative;
          background: #000;
        }

        video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          background: #000;
        }

        .unmute-button {
          position: absolute;
          right: 14px;
          bottom: 14px;
          pointer-events: auto;
          z-index: 2;
          border-radius: 999px;
          overflow: hidden;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
          --media-control-background: rgba(0, 0, 0, 0.55);
          --media-control-hover-background: rgba(0, 0, 0, 0.72);
          --media-button-icon-height: 20px;
        }

        .unmute-button::part(tooltip) {
          display: none;
        }
      </style>
      <video part="video" muted loop playsinline preload="metadata" tabindex="-1" aria-hidden="true"></video>
      <bunny-poster-mute-button part="unmute-button" class="unmute-button" hidden notooltip></bunny-poster-mute-button>
    `;

    const video = shadow.querySelector('video');
    const muteButton = shadow.querySelector('.unmute-button');
    if (video instanceof HTMLVideoElement) {
      this.videoEl = video;
      this.videoEl.controls = false;
      this.videoEl.disablePictureInPicture = true;
      this.videoEl.autoplay = true;
      this.videoEl.muted = true;
      this.videoEl.addEventListener(
        'volumechange',
        () => this.updateMuteButtonState(),
      );
      this.videoEl.addEventListener(
        'loadedmetadata',
        () => this.detectNativeAudioTrack(),
      );
      this.videoEl.addEventListener(
        'timeupdate',
        () => this.detectNativeAudioTrack(),
      );
      this.videoEl.addEventListener(
        'play',
        () => this.detectNativeAudioTrack(),
      );
    }

    if (muteButton instanceof BunnyPosterMuteButton) {
      this.muteButtonEl = muteButton;
      this.muteButtonEl.addEventListener(POSTER_MUTE_TOGGLE_EVENT, (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleMute();
      });
    }

    this.initialized = true;
  }

  private getMediaController(): HTMLElement | null {
    if (typeof this.closest !== 'function') return null;
    return this.closest('media-controller');
  }

  private connectControllerObserver() {
    this.disconnectControllerObserver();
    const controller = this.getMediaController();
    if (!controller) return;

    this.controllerObserver = new MutationObserver(() => {
      this.syncPlaybackState();
    });

    this.controllerObserver.observe(controller, {
      attributes: true,
      attributeFilter: ['data-live', 'data-live-active'],
    });
  }

  private disconnectControllerObserver() {
    if (this.controllerObserver) {
      this.controllerObserver.disconnect();
      this.controllerObserver = null;
    }
  }

  private isDebugLoggingEnabled(): boolean {
    if (this.getAttribute('debug') === 'true') return true;
    try {
      return globalThis.localStorage?.getItem('bunny_player_debug') === 'true';
    } catch {
      return false;
    }
  }

  private debugLogUnexpectedError(
    context: string,
    error: unknown,
    expectedErrorNames: string[] = [],
  ) {
    const errorName = error instanceof DOMException ? error.name : '';
    if (expectedErrorNames.includes(errorName)) return;
    if (!this.isDebugLoggingEnabled()) return;
    console.debug(`[BunnyPosterVideo] ${context}`, error);
  }

  private syncVideoAttributes() {
    if (!this.videoEl) return;

    const src = this.getAttribute(ATTR_SRC);
    const poster = this.getAttribute(ATTR_POSTER);

    this.loadSource(src);

    if (poster) {
      this.videoEl.setAttribute('poster', poster);
    } else {
      this.videoEl.removeAttribute('poster');
    }
  }

  private destroyHls() {
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
  }

  private setHasAudioTrack(hasAudio: boolean) {
    this.hasAudioTrack = hasAudio;
    this.updateMuteButtonVisibility();
    this.updateMuteButtonState();
  }

  private updateMuteButtonVisibility() {
    if (!this.muteButtonEl) return;
    this.muteButtonEl.hidden = !(this.shouldPlay && this.hasAudioTrack);
  }

  private updateMuteButtonState() {
    if (!this.muteButtonEl || !this.videoEl) return;
    const isMuted = this.videoEl.muted || this.videoEl.volume === 0;

    let volumeLevel = 'high';
    if (isMuted) {
      volumeLevel = 'off';
    } else if (this.videoEl.volume < 0.5) {
      volumeLevel = 'low';
    } else if (this.videoEl.volume < 0.75) {
      volumeLevel = 'medium';
    }

    this.muteButtonEl.setAttribute('mediavolumelevel', volumeLevel);
  }

  private detectNativeAudioTrack() {
    if (!this.videoEl || this.hasAudioTrack) return;

    const videoWithAudio = this.videoEl as HTMLVideoElement & {
      audioTracks?: { length?: number };
      mozHasAudio?: boolean;
      webkitAudioDecodedByteCount?: number;
    };

    const hasAudioTracks = (videoWithAudio.audioTracks?.length ?? 0) > 0;
    const hasMozAudio = videoWithAudio.mozHasAudio === true;
    const hasDecodedAudio =
      (videoWithAudio.webkitAudioDecodedByteCount ?? 0) > 0;

    if (hasAudioTracks || hasMozAudio || hasDecodedAudio) {
      this.setHasAudioTrack(true);
    }
  }

  private toggleMute() {
    if (!this.videoEl) return;

    const shouldUnmute = this.videoEl.muted || this.videoEl.volume === 0;
    this.videoEl.muted = !shouldUnmute;

    if (shouldUnmute && this.videoEl.volume === 0) {
      this.videoEl.volume = 1;
    }

    if (shouldUnmute) {
      this.videoEl.play().catch((error) => {
        this.debugLogUnexpectedError(
          'Failed to resume trailer after unmute',
          error,
          ['NotAllowedError', 'AbortError'],
        );
      });
    }

    this.updateMuteButtonState();
  }

  private getPreferredStartLevel(
    levels: Array<{ height?: number }>,
  ): number {
    if (!Array.isArray(levels) || levels.length === 0) return -1;

    let preferredIndex = -1;
    let preferredHeight = 0;
    let maxIndex = 0;
    let maxHeight = levels[0]?.height ?? 0;

    for (let i = 0; i < levels.length; i += 1) {
      const height = levels[i]?.height ?? 0;

      if (height > maxHeight) {
        maxHeight = height;
        maxIndex = i;
      }

      if (height > preferredHeight && height <= PREFERRED_TRAILER_HEIGHT) {
        preferredHeight = height;
        preferredIndex = i;
      }
    }

    return preferredIndex >= 0 ? preferredIndex : maxIndex;
  }

  private canPlayHlsNatively() {
    if (!this.videoEl) return false;
    return this.videoEl.canPlayType('application/vnd.apple.mpegurl') !== '' ||
      this.videoEl.canPlayType('application/x-mpegURL') !== '';
  }

  private loadSource(src: string | null) {
    if (!this.videoEl) return;
    if (src === this.loadedSrc) return;

    this.destroyHls();
    // Trailer should always start muted; user can explicitly unmute via control.
    this.videoEl.muted = true;
    this.setHasAudioTrack(false);
    this.updateMuteButtonState();

    if (!src) {
      this.videoEl.pause();
      this.videoEl.removeAttribute('src');
      this.videoEl.load();
      this.loadedSrc = null;
      return;
    }

    const isHlsSource = src.includes('.m3u8');
    if (isHlsSource && Hls.isSupported()) {
      this.videoEl.removeAttribute('src');
      this.videoEl.load();

      const hls = new Hls({
        debug: false,
        autoStartLoad: false,
      });

      hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
        const hasAudio = (data?.audioTracks?.length ?? 0) > 0 ||
          (data?.levels || []).some((level) =>
            typeof level.audioCodec === 'string' && level.audioCodec.length > 0
          );
        this.setHasAudioTrack(hasAudio);

        const preferredLevel = this.getPreferredStartLevel(data?.levels || []);
        if (preferredLevel >= 0) {
          // Force a decent starting quality so first loop doesn't keep blurry intro segments.
          hls.startLevel = preferredLevel;
          hls.loadLevel = preferredLevel;
        }
        hls.startLoad(0);
      });

      hls.loadSource(src);
      hls.attachMedia(this.videoEl);
      this.hls = hls;
    } else if (isHlsSource && this.canPlayHlsNatively()) {
      // Fallback only when hls.js isn't supported in the current runtime.
      this.videoEl.setAttribute('src', src);
    } else {
      this.videoEl.setAttribute('src', src);
    }

    this.loadedSrc = src;
  }

  private syncPlaybackState() {
    if (!this.videoEl) return;

    const hasSrc = Boolean(this.getAttribute(ATTR_SRC));
    const controller = this.getMediaController();
    const isPlayable = controller?.dataset?.live === 'true';
    const isLiveActive = controller?.dataset?.liveActive === 'true';
    if (isLiveActive) {
      // Once live playback starts in this session, trailer should stay suppressed.
      this.hasSeenLiveActive = true;
    }
    const shouldPlay = hasSrc && !isPlayable && !this.hasSeenLiveActive;
    this.shouldPlay = shouldPlay;

    this.style.display = shouldPlay ? 'block' : 'none';
    this.updateMuteButtonVisibility();
    this.updateMuteButtonState();

    if (!shouldPlay) {
      this.videoEl.pause();
      return;
    }

    this.videoEl.play().catch((error) => {
      this.debugLogUnexpectedError(
        'Failed to autoplay trailer',
        error,
        ['NotAllowedError', 'AbortError'],
      );
    });
  }
}

if (!globalThis.customElements.get('bunny-poster-video')) {
  globalThis.customElements.define('bunny-poster-video', BunnyPosterVideo);
}
